import { BoolQueryParam } from '../../../common/api/Query'
import { v4 as uuidv4 } from 'uuid'
import { UserGender } from '../enums/UserGender'
import { LangType } from '../../../common/enums/LangType'

export interface UserDTO {
  id: string
  firstName: string
  lastName: string
  gender: UserGender
  email: string
  dob: Date
  language: LangType
  isPatient: boolean
  isActive: boolean
  roles: string[]
  patologies: string[]
  notificationsToken: string
  permissions?: string[]
  otherPatology?: string
  phone?: string
  city?: string
  country?: string
  sensitive?: boolean
}

export function emptyUserDTO() {
  return {
    id: uuidv4(),
    firstName: '',
    lastName: '',
    gender: UserGender.Female,
    email: '',
    dob: new Date(),
    language: LangType.Spanish,
    isPatient: true,
    isActive: true,
    roles: [],
    patologies: [],
    notificationsToken: '',
    permissions: [],
    otherPatology: '',
    phone: '',
    city: '',
    country: '',
    sensitive: false,
  }
}

export class User {
  private readonly _id: string
  private readonly _isPatient: boolean

  private _firstName: string
  private _lastName: string
  private _gender: UserGender
  private _email: string
  private _dob: Date
  private _language: LangType
  private _isActive: boolean
  private readonly _roles: string[]
  private readonly _patologies: string[]
  private _notificationsToken: string
  private _permissions?: string[]
  private _otherPatology?: string
  private _phone: string
  private _city: string
  private _country: string
  private _sensitive: boolean

  constructor(p: UserDTO) {
    this._id = p.id
    this._firstName = p.firstName
    this._lastName = p.lastName
    this._gender = p.gender
    this._email = p.email
    this._dob = p.dob
    this._language = p.language
    this._isPatient = p.isPatient
    this._isActive = p.isActive
    this._roles = p.roles
    this._patologies = p.patologies
    this._notificationsToken = p.notificationsToken
    this._permissions = p.permissions
    this._otherPatology = p.otherPatology
    this._phone = p.phone ?? ''
    this._city = p.city ?? ''
    this._country = p.country ?? ''
    this._sensitive = p.sensitive ?? false
  }

  get roles(): string[] {
    return this._roles
  }

  get patologies(): string[] {
    return this._patologies
  }

  get id(): string {
    return this._id
  }

  get isPatient(): boolean {
    return this._isPatient
  }

  get firstName(): string {
    return this._firstName
  }

  set firstName(value: string) {
    this._firstName = value
  }

  get lastName(): string {
    return this._lastName
  }

  set lastName(value: string) {
    this._lastName = value
  }

  get gender(): UserGender {
    return this._gender
  }

  set gender(value: UserGender) {
    this._gender = value
  }

  get email(): string {
    return this._email
  }

  set email(value: string) {
    this._email = value
  }

  get dob(): Date {
    return this._dob
  }

  set dob(value: Date) {
    this._dob = value
  }

  get language(): LangType {
    return this._language
  }

  set language(value: LangType) {
    this._language = value
  }

  get isActive(): boolean {
    return this._isActive
  }

  set isActive(value: boolean) {
    this._isActive = value
  }

  get notificationsToken(): string {
    return this._notificationsToken
  }

  set notificationsToken(value: string) {
    this._notificationsToken = value
  }

  get permissions(): string[] | undefined {
    return this._permissions
  }

  set permissions(value: string[] | undefined) {
    this._permissions = value
  }

  get otherPatology(): string | undefined {
    return this._otherPatology
  }

  set otherPatology(value: string | undefined) {
    this._otherPatology = value
  }

  get phone(): string {
    return this._phone
  }

  set phone(value: string) {
    this._phone = value
  }

  get city(): string {
    return this._city
  }

  set city(value: string) {
    this._city = value
  }

  get country(): string {
    return this._country
  }

  set country(value: string) {
    this._country = value
  }

  get sensitive(): boolean {
    return this._sensitive
  }

  set sensitive(value: boolean) {
    this._sensitive = value
  }

  toDTO(): UserDTO {
    return {
      id: this.id,
      firstName: this.firstName,
      lastName: this.lastName,
      gender: this.gender,
      email: this.email,
      dob: this.dob,
      language: this.language,
      isPatient: this.isPatient,
      isActive: this.isActive,
      roles: this.roles,
      patologies: this.patologies,
      notificationsToken: this.notificationsToken,
      permissions: this.permissions,
      otherPatology: this.otherPatology,
      phone: this.phone,
      city: this.city,
      country: this.country,
      sensitive: this.sensitive,
    }
  }
}

export interface UserQuery {
  id: string
  ids: string[]
  isPatient: BoolQueryParam
  firstName: string
  lastName: string
  isActive: BoolQueryParam
}

export function toModel(d: UserDTO): User {
  return new User(d)
}

export function fromModel(d: User): UserDTO {
  return {
    id: d.id,
    firstName: d.firstName,
    lastName: d.lastName,
    gender: d.gender,
    email: d.email,
    dob: d.dob,
    language: d.language,
    isPatient: d.isPatient,
    isActive: d.isActive,
    roles: d.roles,
    patologies: d.patologies,
    notificationsToken: d.notificationsToken,
    permissions: d.permissions,
    otherPatology: d.otherPatology,
    phone: d.phone,
    city: d.city,
    country: d.country,
    sensitive: d.sensitive,
  }
}
